import { getBootstrapData } from '@experience/core-hostapp-share-lib';
import {
  sendFavoriteMindboxEvent,
  type FavoriteMindboxExperience,
  type FavoriteMindboxPrice,
} from '@experience/mindbox-events';
import { defineStore } from 'pinia';

import {
  addExperienceToFavorite,
  getExperienceList,
  getExperiencesFavoritesList,
  getMovementTypes,
  removeExperienceFromFavorite,
  type ExperienceFavoriteItem,
  type MovementType,
} from '@/shared/api/experience';
import { useLogger } from '@/shared/utils/logger';

import type { ExperienceWithFavorite, Experience } from './models';
import { normalizeExperience } from './transforms';

// Ограничивает favorite-действия полями общей Mindbox-модели без legacy-полей карточки.
interface FavoriteToggleExperience {
  id: number;
  photos?: FavoriteMindboxExperience['photos'];
  price?: Pick<FavoriteMindboxPrice, 'value' | 'per_group'>;
}

export interface LoadParams {
  type?: string;
  city?: number;
  citySlugs?: string[];
  startDuration?: number;
  endDuration?: number;
  sorting?: 'rank' | 'published_at' | 'rating' | 'price';
  offset?: number;
  limit?: number;
  priceFormat?: string;
  extendedMovementTypes?: boolean;
  photosCount?: number;
  countrySlug?: string;
  regionSlug?: string;
}

interface State {
  experienceLoading: boolean;
  experienceLoadingFailed: boolean;
  experiencesState: Experience[];
  favoritesState: ExperienceFavoriteItem[];

  /**
   * Флаг, предотвращающий конфликты при множественных изменениях в избранном.
   */
  favoritesToggling: boolean;
}

export const useExperienceEntity = defineStore('journal:experience-entity', {
  state: (): State => ({
    /**
     * Флаг индикации загрузки.
     */
    experienceLoading: false,

    /**
     * Флаг, показывающий, что загрузка окончилать неудачей.
     */
    experienceLoadingFailed: false,

    /**
     * Список экскурсий и туров.
     */
    experiencesState: [] as Experience[],

    /**
     * Список избранного.
     */
    favoritesState: [] as ExperienceFavoriteItem[],

    /**
     * Флаг, предотвращающий конфликты при множественных изменениях в избранном.
     */
    favoritesToggling: false,
  }),

  getters: {
    /**
     * Флаг индикации загрузки.
     */
    loading: (state: State) => state.experienceLoading,

    /**
     * Флаг, показывающий, что загрузка окончилать неудачей.
     */
    loadingFailed: (state: State) => state.experienceLoadingFailed,

    /**
     * Список экскурсий и туров с указанием флага "находится в избранном".
     */
    experiencesWithFavorite: (state: State): ExperienceWithFavorite[] =>
      state.experiencesState.map((exp) => ({
        exp,
        favorite: state.favoritesState.findIndex((favorite) => favorite.item_id === exp.id) !== -1,
      })),
  },

  actions: {
    logger: useLogger,

    /**
     * Загружает список экскурсий и туров.
     *
     * @param params параметры запроса
     */
    async load(params: LoadParams) {
      this.experienceLoading = true;
      this.experienceLoadingFailed = false;

      let response;
      let favorites;
      let movementTypes: Awaited<ReturnType<typeof getMovementTypes>>;

      try {
        [response, favorites, movementTypes] = await Promise.all([
          getExperienceList({
            query: {
              city: params.city,
              city__slug: params.citySlugs?.join(','),
              country__slug: params.countrySlug,
              region__slug: params.regionSlug,
              end_duration: params.endDuration,
              start_duration: params.startDuration,
              type: params.type,
              sorting: params.sorting,
              limit: params.limit,
              offset: params.offset,
              price_format: params.priceFormat,
              extended_movement_types: params.extendedMovementTypes,
              photos_count: params.photosCount,
            },
          }),
          getExperiencesFavoritesList(),
          getMovementTypes(),
        ]);
      } catch (e) {
        this.logger().error('could not load an experiences list by the reason:', e);
        this.experienceLoadingFailed = true;
        this.experienceLoading = false;

        return;
      }

      if (!response?.data) {
        this.logger().error('could not load an experiences list, status code:', response.response.status);
        this.experienceLoadingFailed = true;
        this.experienceLoading = false;

        return;
      }

      this.experiencesState = response.data.results.map((exp) => {
        const types = movementTypes.data?.movement_type;
        if (types) {
          const movementTypeObj = types.find((el: MovementType) => el.str === exp.movement_type);
          exp.movement_type = movementTypeObj ? movementTypeObj.label_phrase : '';
        }

        return normalizeExperience(exp);
      });

      this.favoritesState = favorites.data ?? [];
      this.experienceLoading = false;
    },

    /**
     * Подгружает список избранного (без списка экскурсий), например для страницы статьи.
     */
    async loadFavorites() {
      try {
        const favorites = await getExperiencesFavoritesList();
        this.favoritesState = favorites.data ?? [];
      } catch (error) {
        this.logger().error('could not load favorites list:', error);
      }
    },

    /**
     * Сбрасывает состояние экземпляра сущности.
     */
    reset() {
      this.experienceLoading = false;
      this.experienceLoadingFailed = false;
      this.experiencesState = [];
      this.favoritesState = [];
      this.favoritesToggling = false;
    },

    /**
     * Добавляет экскурсий или тур в избранное.
     *
     * @param experience экскурсия/тур для добавления
     */
    async setFavorite(experience: FavoriteToggleExperience) {
      if (this.favoritesToggling) {
        return;
      }

      const experienceId = experience.id;
      let favoriteItem;

      this.favoritesToggling = true;

      try {
        // Фиксирует исходное пользовательское действие до favorite API для auth и guest.
        sendFavoriteMindboxEvent({
          action: 'add',
          bootstrapData: getBootstrapData(),
          experience,
        });
        favoriteItem = await addExperienceToFavorite({ path: { experienceId } });
      } catch (e) {
        this.favoritesToggling = false;

        throw e;
      }

      if (favoriteItem.data) {
        this.favoritesState = [...this.favoritesState, favoriteItem.data];
      }

      this.favoritesToggling = false;
    },

    /**
     * Удаляет экскурсию или тура из избранного.
     *
     * @param experience экскурсия/тур для удаления
     */
    async unsetFavorite(experience: FavoriteToggleExperience) {
      if (this.favoritesToggling) {
        return;
      }

      const experienceId = experience.id;

      this.favoritesToggling = true;

      try {
        // Фиксирует исходное пользовательское действие до favorite API для auth и guest.
        sendFavoriteMindboxEvent({
          action: 'remove',
          bootstrapData: getBootstrapData(),
          experience,
        });
        await removeExperienceFromFavorite({ path: { experienceId } });
      } catch (e) {
        this.favoritesToggling = false;

        throw e;
      }

      this.favoritesState = this.favoritesState.filter((item) => item.item_id !== experienceId);
      this.favoritesToggling = false;
    },
  },
});
